import { Notification as NotificationBase } from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useAgency, { UseAgencyPayload } from '@hooks/useAgency'
import useCurrentUser from '@hooks/useCurrentUser'
import useEntityImpersonation, { UseEntityImpersonationPayload } from '@hooks/useEntityImpersonation'
import useNotification, { UseNotificationPayload } from '@hooks/useNotification'

import handleCallbackAction from '@functions/handleCallbackAction'
import formatRelativeDate from '@functions/formatRelativeDate'

import type { NotificationModel } from '@models/types'

type BuildOnClickParams = {
  callbacks: {
    generateTokenAndImpersonate: UseEntityImpersonationPayload['callbacks']['generateTokenAndImpersonate'],
    loginAsAgency: UseAgencyPayload['callbacks']['loginAsAgency'],
  },
  isAdmin: boolean,
  notificationPayload: UseNotificationPayload,
}

const buildOnClick = (params: BuildOnClickParams) => {
  const {
    callbacks: { generateTokenAndImpersonate, loginAsAgency },
    isAdmin,
    notificationPayload,
  } = params

  const {
    callbacks: { buildNotificationLink },
    notification: { link, notifiable_type },
    notifiable,
  } = notificationPayload

  const loginAsAgencyPayload = {
    callbacks: {
      action: loginAsAgency,
      buildActionParams: () => [{ redirect: link }],
    },
    shouldRedirect: true,
    toastText: 'Logged In',
  }

  if (!link || !isAdmin || notifiable_type !== 'Agency') return undefined

  if (notifiable?.realhub_platform !== 'standalone_v1'){
    // @ts-expect-error TS2345
    return () => handleCallbackAction(loginAsAgencyPayload)
  }

  return () => generateTokenAndImpersonate({ entity: notifiable, redirectPath: buildNotificationLink() })
}

const Notification = (props: { notification: NotificationModel }) => {
  const {
    notification,
    notification: { notifiable_id },
  } = props

  const notificationPayload = useNotification(notification)
  const {
    callbacks: { buildNotificationLink },
  } = notificationPayload

  const { isAdmin } = useCurrentUser()

  const {
    agency,
    callbacks: { loginAsAgency },
  } = useAgency({ id: notifiable_id })

  // @ts-expect-error TS2345
  const { callbacks: { generateTokenAndImpersonate } } = useEntityImpersonation(agency)

  const createdAt = digObject(notification, 'dates.created.date_time', '')
  const logoUrl = digObject(notification, 'creator.logo.thumb', '')

  return (
    <NotificationBase
      callbacks={{
        onClick: buildOnClick({
          callbacks: { generateTokenAndImpersonate, loginAsAgency },
          isAdmin,
          notificationPayload,
        }),
      }}
      logoUrl={logoUrl}
      notification={{ ...notification, link: buildNotificationLink() }}
      // @ts-expect-error TS2322
      relativeTime={formatRelativeDate(createdAt)}
    />
  )
}

export default Notification
